import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Desktop bottom-menu collapse uses scroll hysteresis so tiny scroll / sticky
   * intersection flicker near the top doesn't thrash open ↔ closed.
   * Collapse only after a real scroll; expand only when back at the very top.
   */
  static #MENUS_COLLAPSE_AT = 220;
  static #MENUS_EXPAND_AT = 0;

  /** Ignore expand/collapse flips while the height animation is running */
  static #MENUS_LOCK_MS = 500;

  /** @type {number} */
  #menusLockUntil = 0;

  /** Cached bottom-row height (px) used to push collapse further past the menu itself */
  /** @type {number} */
  #bottomRowHeight = 0;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        // sticky-state / scroll-direction are owned by scroll hysteresis in
        // #updateStickyAlwaysAttrs — intersection alone thrash-flips near top: -1px
        // when the bottom menu height animates.
        if (!isIntersecting && this.dataset.themeColor) {
          changeMetaThemeColor(this.dataset.themeColor);
        }
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
      // The drawer squeeze can trigger minimum-reached at desktop widths where
      // it normally wouldn't. Once the menu hides, the overflow-list is
      // display:none and can't measure to clear it. Resetting it here so
      // setHeaderMenuStyle() sees a clean state.
      const overflowList = this.querySelector('overflow-list');
      if (overflowList) overflowList.removeAttribute('minimum-reached');
    }
    setHeaderMenuStyle();
  }

  /**
   * Rebinds the scroll listener and IntersectionObserver when the viewport
   * crosses the squeeze breakpoint (990px). The scroll container switches
   * between `.page-wrapper` (desktop) and `document.scrollingElement` (mobile),
   * so cached bindings from initialization become stale after a resize.
   */
  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    // Rebind scroll listener
    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
    }

    // Recreate IntersectionObserver with the new root
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#observeStickyPosition(stickyMode === 'always');
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  /**
   * Collapse threshold: at least 220px, or bottom-menu height + buffer (whichever larger).
   * @returns {number}
   */
  #menusCollapseAt() {
    if (this.dataset.menusCollapsed !== 'true') {
      const row = this.querySelector('.header__row--bottom');
      if (row instanceof HTMLElement) {
        const h = row.getBoundingClientRect().height;
        if (h > 0) this.#bottomRowHeight = h;
      }
    }
    return Math.max(HeaderComponent.#MENUS_COLLAPSE_AT, Math.round(this.#bottomRowHeight) + 140);
  }

  /**
   * Collapse / expand bottom menu row based on scroll position with hysteresis.
   * @param {number} scrollTop
   */
  #updateMenusCollapsed = (scrollTop) => {
    // Mobile keeps the bottom row (it's already hidden via .mobile:hidden markup).
    if (window.matchMedia('(max-width: 749px)').matches) {
      if (this.dataset.menusCollapsed === 'true') {
        this.dataset.menusCollapsed = 'false';
      }
      return;
    }

    if (performance.now() < this.#menusLockUntil) return;

    const collapsed = this.dataset.menusCollapsed === 'true';
    const collapseAt = this.#menusCollapseAt();
    const expandAt = HeaderComponent.#MENUS_EXPAND_AT;

    if (!collapsed && scrollTop >= collapseAt) {
      this.dataset.menusCollapsed = 'true';
      this.#menusLockUntil = performance.now() + HeaderComponent.#MENUS_LOCK_MS;
    } else if (collapsed && scrollTop <= expandAt) {
      this.dataset.menusCollapsed = 'false';
      this.#menusLockUntil = performance.now() + HeaderComponent.#MENUS_LOCK_MS;
    }
  };

  /**
   * For sticky=always: drive sticky-state + scroll-direction from scrollTop only.
   * Never use getBoundingClientRect().top — menu height animation makes it oscillate.
   * scroll-direction stays at 'none' | 'down' (no up/down thrash); logo only needs ≠ none.
   * @param {number} scrollTop
   */
  #updateStickyAlwaysAttrs = (scrollTop) => {
    if (performance.now() < this.#menusLockUntil) return;

    const collapseAt = this.#menusCollapseAt();
    const expandAt = HeaderComponent.#MENUS_EXPAND_AT;

    if (scrollTop <= expandAt) {
      if (this.dataset.scrollDirection !== 'none') {
        this.dataset.scrollDirection = 'none';
      }
      if (this.dataset.stickyState !== 'inactive') {
        this.dataset.stickyState = 'inactive';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      }
      return;
    }

    if (scrollTop >= collapseAt) {
      if (this.dataset.scrollDirection === 'none') {
        this.dataset.scrollDirection = 'down';
      }
      if (this.dataset.stickyState !== 'active') {
        this.dataset.stickyState = 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      }
      return;
    }

    // Hysteresis band (expandAt … collapseAt): keep current attrs, don't thrash
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    const scrollTop = getScrollTop();

    // Always keep menu collapse in sync while sticky is enabled (incl. at top).
    if (stickyMode) {
      this.#updateMenusCollapsed(scrollTop);
    }

    if (stickyMode === 'always') {
      this.#updateStickyAlwaysAttrs(scrollTop);
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (!this.#offscreen) {
      this.#lastScrollTop = scrollTop;
      return;
    }

    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop + 0.5 < this.#lastScrollTop;
    // Prefer scrollTop over headerTop — headerTop flickers when height animates
    const isAtTop = scrollTop <= HeaderComponent.#MENUS_EXPAND_AT || headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#scrollContainer = getScrollEventTarget();
        this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
      }

      scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
      this.#lastScrollTop = getScrollTop();
      this.#updateMenusCollapsed(this.#lastScrollTop);
      if (stickyMode === 'always') {
        this.#updateStickyAlwaysAttrs(this.#lastScrollTop);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
